import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { I18nModule, Product, ProductScope, TranslationService, WindowRef } from '@spartacus/core';
import { CurrentProductService, OutletModule, ProductDetailOutlets, SpinnerModule } from '@spartacus/storefront';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { GenProductService } from 'src/app/generac/services/product.service';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GenMSRPPrice } from 'src/app/generac/models/product.model';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { CommonModule } from '@angular/common';
import { GenSpIconsComponent } from 'src/app/generac/shared/components/sp-icons/sp-icons.component';
import { SafeModule } from 'src/app/generac/shared/pipes/safe/safe.module';
import { GenSharedModule } from 'src/app/generac/shared/shared.module';
import { GenNoReturnWarningComponent } from '../no-return-warning/no-return-warning.component';
import { GenSpInfoComponent } from '../sp-info/sp-info.component';
import { GenProductBadgeComponent } from '../../../shared/components/product-badge/product-badge.component';

@Component({
    selector: 'gen-product-summary',
    templateUrl: './product-summary.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      CommonModule,
      OutletModule,
      I18nModule,
      SpinnerModule,
      GenNoReturnWarningComponent,
      SafeModule,
      GenSharedModule,
      GenSpInfoComponent,
      GenSpIconsComponent,
      GenProductBadgeComponent,
    ],
})
export class GenProductSummaryComponent implements OnInit, OnDestroy {
  outlets = ProductDetailOutlets;
  product$: Observable<Product | null> =
    this.currentProductService.getProduct([
      ProductScope.DETAILS,
      ProductScope.PRICE,
    ]);
  currentPrice: GenMSRPPrice;
  noNetPrice = false;
  exclusionMessage: string;
  atestationDataRestrictionMessage: string;
  scratchAndDentWarrantyInfo: string;
  navigatedToPpdFrom: string;
  product: Product;
  purchasable: boolean;
  isMobileView: boolean;
  isScratchAndDentOrReconditioned : boolean;
  gaEntrySource: string;
  private subscriptions = new Subscription();


  @HostListener('window:resize')
  onResize() {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 992;
  }

  constructor(
    protected currentProductService: CurrentProductService,
    protected customerService: GenCustomerService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected activatedRoute: ActivatedRoute,
    private userAccount: UserAccountFacade,
    private productService: GenProductService,
    private cdr: ChangeDetectorRef,
    protected baseStoreService: GenBaseStoreService,
    protected winRef: WindowRef,
    protected translation: TranslationService,
  ) {}

  ngOnInit(): void {
    this.subscribeToCurrentPrice();
    this.updateRecentlyViewed();
    this.subscribeToContactInfoAndTooltip();
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 992;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateRecentlyViewed() {
    this.subscriptions.add(
      combineLatest([
        this.userAccount.get(),
        this.product$,
      ]).pipe(
        switchMap(([_, product]) => {
          this.product = product;

          this.isScratchAndDentOrReconditioned  = this.isScratchAndDentOrReconditionedProduct();
          this.currentPrice = null;
          this.productService.cpiPriceRes$$.next(null);
          this.purchasable = product.purchasable;
          return this.productService.updateRecentlyViewed(product.code);
        })
      ).subscribe()
    )
  }

  private isScratchAndDentOrReconditionedProduct(): boolean {
    return this.product.badges?.some(
      badge => badge.code === 'scratch-and-dent' || badge.code === 'reconditioned'
    ) ?? false;
  }
  
  private subscribeToContactInfoAndTooltip(): void {
    this.subscriptions.add(
      combineLatest([
        this.baseStoreService.getPhoneNumber(),
        this.translation.translate('productDetails.atestationDataRestrictionMessage'),
        this.translation.translate('productDetails.scratchAndDentWarrantyInfo'),
      ]).subscribe(([phoneOrEmail, atestationDataRestrictionMessage, scratchAndDentWarrantyInfo]) => {
        this.atestationDataRestrictionMessage = atestationDataRestrictionMessage;
        this.scratchAndDentWarrantyInfo = scratchAndDentWarrantyInfo;
        this.exclusionMessage = `This material is not currently available to you based on regional restrictions. Please contact your Sales Representative for more information via ${phoneOrEmail.contactInfo}`
        this.cdr.markForCheck();
      })
    );
  }

  private subscribeToCurrentPrice() {
    this.subscriptions.add(
      combineLatest([
        this.userAccount.get(),
        this.product$,
        this.customerService.getB2bUnitChanged(),
      ]).pipe(
        switchMap(([user, product]) => {
          return combineLatest([product.unavailabilityDetail ? of(null): this.productService
            .getCPIPrice(user?.uid, product)
            .pipe(
              tap(currentPrice => {
                this.currentPrice = currentPrice;
                this.cdr.markForCheck();
              }),
              catchError(() => {
                this.currentPrice = null;
                this.noNetPrice = true;
                this.cdr.markForCheck();
                return of(null);
              })), of(product)
          ])
        }),
        distinctUntilChanged((prev, curr) => prev[1].code === curr[1].code)
      ).subscribe(([currentPrice, product]) => {
        if(this.activatedRoute.snapshot.queryParams?.gaListName) {
          this.navigatedToPpdFrom = this.activatedRoute.snapshot.queryParams?.gaListName;
        }
        if(this.activatedRoute.snapshot.queryParams?.entry_source) {
          this.gaEntrySource = this.activatedRoute.snapshot.queryParams?.entry_source;
        }
        this.googleAnalyticsService.sendViewItemListEvent([{...product, price: currentPrice }], GenGaListNames.PDP);

        this.googleAnalyticsService.sendGaEvent('view_item', {
          items: [ this.googleAnalyticsService.mapProductToGaItem({...product, price: currentPrice }, 0, undefined, undefined, this.navigatedToPpdFrom, this.gaEntrySource) ],
          currency: product.price?.currencyIso,
        });
      })
    )
  }
}
